import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { useParams } from "react-router-dom";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { PulseLoader } from 'react-spinners';
import { ToastContainer } from "react-toastify";
import {
  getBookingSlot,
  updateBookingSlot,
  deleteBookingSlot,
  createBookingSlot,
  getAllTicketGroupBySlug,
} from "../../../api";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
} from "reactstrap";
import PageHeader from "../../../components/common/PageHeader/PageHeader";
import ListCard from "../../../components/common/ListCard/ListCard";
import { Search } from "lucide-react";
import { useNightMode } from "../../../contexts/NightModeContext";
import Background from "../../Login/Background";
import ConfirmationDialog from "../../../components/common/ConfirmationDialog/ConfirmationDialog";

const UserSlot = () => {
  const { isNightMode } = useNightMode();
  const [slots, setSlots] = useState<any[]>([]);
  const [ticketGroups, setTicketGroups] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { slugId } = useParams();
  const [modal, setModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [editData, setEditData] = useState<any>(null);
  const [search, setSearch] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    slot: any;
  }>({ isOpen: false, slot: null });

  const getErrorMessage = (error: any) => typeof error === "string" ? error : "";
  const toggle = () => setModal(!modal);
  const toggleEditModal = () => {
    setEditModal(!editModal);
  };

  const getTicketList = async () => {
    const eventData = localStorage.getItem("eventData");
    setIsLoading(true);
    try {
      if (eventData) {
        const eventSlug = JSON.parse(eventData).slugId;
        const [slotResponse, ticketGroupsResponse] = await Promise.all([
          getBookingSlot(eventSlug),
          getAllTicketGroupBySlug(eventSlug).catch((err) => {
            console.error("Failed to fetch ticket groups:", err);
            return [];
          })
        ]);
        setSlots(slotResponse || []);
        setTicketGroups(ticketGroupsResponse || []);
      }
    } catch (error) {
      console.error("Failed to fetch slot data:", error);
      toast.error("Unable to load slot data.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getTicketList();
  }, [slugId]);

  const handleDeleteClick = (slot: any) => {
    setDeleteConfirm({ isOpen: true, slot });
  };

  const confirmDelete = async () => {
    const slot = deleteConfirm.slot;
    if (!slot) return;
    try {
      setIsLoading(true);
      await deleteBookingSlot(slot.id);
      toast.success("Slot deleted successfully!");
      getTicketList();
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || "Failed to delete slot.";
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
      setDeleteConfirm({ isOpen: false, slot: null });
    }
  };

  const handleEditClick = (item: any) => {
    setEditData(item);
    setEditModal(true);
  };

  const handleSearch = (e: any) => {
    const searchVal = e.target.value;
    setSearch(searchVal);
    if (searchVal.length > 0) {
      const filtered = slots.filter((slot: any) =>
        slot.slotName?.toLowerCase().includes(searchVal.toLowerCase())
      );
      setSlots(filtered);
    } else {
      getTicketList();
    }
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      slotName: "",
      totalCount: "",
      ticketGroupId: "",
      status: "active",
    },
    validationSchema: Yup.object({
      slotName: Yup.string().required("Slot name is required"),
      totalCount: Yup.number()
        .integer("Total count must be an integer")
        .min(1, "Total count must be at least 1")
        .required("Total count is required"),
      ticketGroupId: Yup.string().required("Ticket group is required"),
      status: Yup.string().required("Status is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        setIsLoading(true);
        const eventData = localStorage.getItem("eventData");
        if (eventData) {
          const eventSlugId = JSON.parse(eventData).slugId;
          const payload = {
            slotName: values.slotName,
            totalCount: Number(values.totalCount),
            ticketGroupId: Number(values.ticketGroupId),
            slugId: eventSlugId,
            status: values.status,
          };
          await createBookingSlot(payload);
          toast.success("Slot created successfully!");
          
          toggle();
          resetForm();
          getTicketList();
        }
      } catch (error: any) {
        const errorMsg = error.response?.data?.message || "Failed to create slot.";
        toast.error(errorMsg);
      } finally {
        setIsLoading(false);
      }
    },
  });

  const editValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      slotName: editData?.slotName || "",
      status: editData?.status || "active",
    },
    validationSchema: Yup.object({
      slotName: Yup.string().required("Slot name is required"),
      status: Yup.string().required("Status is required"),
    }),
    onSubmit: async (values) => {
      try {
        setIsLoading(true);
        const payload = {
          slotName: values.slotName,
          status: values.status,
        };

        await updateBookingSlot(editData.id, payload);
        toast.success("Slot updated successfully!");
        
        setEditModal(false);
        getTicketList();
      } catch (error: any) {
        const errorMsg = error.response?.data?.message || "Failed to update slot.";
        toast.error(errorMsg);
      } finally {
        setIsLoading(false);
      }
    }
  });

  return (
    <div className={`contact-list-container ${isNightMode ? 'text-white' : 'text-dark'}`}>
      <Background />
      <ToastContainer autoClose={2000} />
      <PageHeader
        title="Slot Bookings"
        subtitle="Manage and categorize event booking slots and allocations."
        isLoading={isLoading}
        actions={
          <Button color="danger" onClick={toggle} className="v3-btn-primary h_40 d-flex align-items-center">
            <i className="fa-solid fa-plus me-2"></i> New Slot
          </Button>
        }
      />

      <div className="contact-filter-card mb-4 mt-4">
        <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-3">
          <div className="position-relative flex-grow-1 w-100">
            <Search className="position-absolute start-0 top-50 translate-middle-y ms-3 contact-sub-text" size={18} />
            <input
              type="text"
              className="contact-search-input"
              placeholder="Search by slot name..."
              value={search}
              onChange={handleSearch}
            />
          </div>
        </div>
      </div>

      <div>
        <Modal isOpen={modal} toggle={toggle}>
          <ModalHeader toggle={toggle}>Add New Slot</ModalHeader>
          <Form onSubmit={validation.handleSubmit}>
            <ModalBody className="px-4">
              <FormGroup>
                <Label className="form-label">Slot Name</Label>
                <Input
                  name="slotName"
                  type="text"
                  placeholder="Slot Name"
                  value={validation.values.slotName}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={validation.touched.slotName && !!validation.errors.slotName}
                />
                <FormFeedback>{getErrorMessage(validation.errors.slotName)}</FormFeedback>
              </FormGroup>

              <FormGroup>
                <Label className="form-label">Total Count</Label>
                <Input
                  name="totalCount"
                  type="number"
                  placeholder="Total Count"
                  value={validation.values.totalCount}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={validation.touched.totalCount && !!validation.errors.totalCount}
                />
                <FormFeedback>{getErrorMessage(validation.errors.totalCount)}</FormFeedback>
              </FormGroup>

              <FormGroup>
                <Label className="form-label">Ticket Group</Label>
                <Input
                  name="ticketGroupId"
                  type="select"
                  value={validation.values.ticketGroupId}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={validation.touched.ticketGroupId && !!validation.errors.ticketGroupId}
                >
                  <option value="">Select Ticket Group</option>
                  {ticketGroups.map((group: any) => (
                    <option key={group.id} value={group.id}>
                      {group.groupName}
                    </option>
                  ))}
                </Input>
                <FormFeedback>{getErrorMessage(validation.errors.ticketGroupId)}</FormFeedback>
              </FormGroup>

              <FormGroup>
                <Label className="form-label">Status</Label>
                <Input
                  name="status"
                  type="select"
                  value={validation.values.status}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={validation.touched.status && !!validation.errors.status}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="hidden">Hidden</option>
                </Input>
                <FormFeedback>{getErrorMessage(validation.errors.status)}</FormFeedback>
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button className="btn btn-primary mt-3 w-100 h_50" color="primary" type="submit" disabled={isLoading}>
                <h5>
                  {isLoading ? "Saving..." : "Save Changes"}
                </h5>
              </Button>
            </ModalFooter>
          </Form>
        </Modal>

        <Modal isOpen={editModal} toggle={toggleEditModal}>
          <ModalHeader toggle={toggleEditModal}>Edit Slot</ModalHeader>
          <Form onSubmit={editValidation.handleSubmit}>
            <ModalBody className="px-4">
              <FormGroup>
                <Label className="form-label">Slot Name</Label>
                <Input
                  name="slotName"
                  type="text"
                  value={editValidation.values.slotName}
                  onChange={editValidation.handleChange}
                  onBlur={editValidation.handleBlur}
                  invalid={editValidation.touched.slotName && !!editValidation.errors.slotName}
                />
                <FormFeedback>{getErrorMessage(editValidation.errors.slotName)}</FormFeedback>
              </FormGroup>

              <FormGroup>
                <Label className="form-label">Status</Label>
                <Input
                  name="status"
                  type="select"
                  value={editValidation.values.status}
                  onChange={editValidation.handleChange}
                  onBlur={editValidation.handleBlur}
                  invalid={editValidation.touched.status && !!editValidation.errors.status}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="hidden">Hidden</option>
                </Input>
                <FormFeedback>{getErrorMessage(editValidation.errors.status)}</FormFeedback>
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button className="btn btn-primary mt-3 w-100 h_50" color="primary" type="submit" disabled={isLoading}>
                <h5>
                  {isLoading ? "Saving..." : "Save Changes"}
                </h5>
              </Button>
            </ModalFooter>
          </Form>
        </Modal>

        <ConfirmationDialog
          isOpen={deleteConfirm.isOpen}
          toggle={() => setDeleteConfirm({ isOpen: false, slot: null })}
          onConfirm={confirmDelete}
          title="Delete Slot"
          message={`Are you sure you want to permanently delete <strong>${deleteConfirm.slot?.slotName || 'this slot'}</strong>? This action cannot be undone.`}
          confirmText="Yes, Delete"
          cancelText="Cancel"
          type="danger"
          icon="fa-solid fa-trash"
        />

      </div>

      {isLoading ? (
        <div className="d-flex justify-content-center py-4">
          <PulseLoader color="#ffffff" size={9} speedMultiplier={1} />
        </div>
      ) : (
        <div className="mt-4">
          {slots.length > 0 ? (
            slots.map((slot: any) => (
              <ListCard
                key={slot.id}
                cardData={{
                  icon: "fa-solid fa-calendar-day",
                  name: slot.slotName,
                  startDate: slot.createdAt,
                  status: slot.status,
                  isActive: slot.status === "active",
                  optionHead: "Total Count",
                  option: slot.totalCount?.toString(),
                  optionHead1: "Booked Count",
                  option1: slot.bookedCount?.toString(),
                  optionIcon1: "fa-solid fa-users",
                  optionHead2: "Remaining Slot",
                  option2: slot.remainingSlot?.toString(),
                  optionIcon2: "fa-solid fa-calendar-check",
                  ticketGroup: slot.groupName,
                }}
                onEdit={() => handleEditClick(slot)}
                onDelete={() => handleDeleteClick(slot)}
              />
            ))
          ) : (
            <p className="text-muted">No slot bookings available</p>
          )}
        </div>
      )}
    </div>
  );
};

export default UserSlot;